<template>
  <div
    v-if="dsComponentDetails.version === VersionOptions.LEGACY"
    data-sb-regression="sb-checkbox"
    :class="['sb-checkbox', variationClass]"
  >
    <label
      :class="labelClass"
    >
      <div :class="['checkmark-wrapper']">
        <input
          :checked="canCheck"
          class="form-check-input"
          :data-sb-automation="$attrs['data-sb-automation'] || 'checkbox-input'"
          type="checkbox"
          v-bind="$attrs"
          :disabled="disabled"
          @change.prevent="inputChanged"
        >
        <i
          aria-hidden="true"
          class="checkmark"
        />
        <div
          v-if="$slots.default"
          class="label-slot"
        >
          <!--
            @slot text, components, or HtmlElements as children of the component
          -->
          <slot />
        </div>
      </div>
    </label>
  </div>
  <SbCheckboxNext
    v-else
    :checked="checked"
    :indeterminate="indeterminate"
    :value="value"
    :variation="variation"
    :disabled="disabled"
    @update:checked="inputChangedNext($event)"
  >
    <slot />
  </SbCheckboxNext>
</template>

<script>
/**
 * Checkboxes are usually suitable for lists when giving
 * permissions is necessary. This checkbox has a blue
 * theme and changes when clicked.
 */
import isEqual from 'lodash/isEqual';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';
import SbCheckboxNext from '@src/components/Checkbox/SbCheckboxNext.vue';

export default {
  name: 'SbCheckbox',
  components: {
    SbCheckboxNext,
  },
  mixins: [versionedComponentsMixin],
  // model: {
  //   prop: 'checked',
  //   event: 'change',
  // },
  props: {
    /**
     * It is the type that the checkbox is modeled on. You use it as v-model="putYourObjHere" that you want it to work from.
     */
    checked: {
      type: [String, Boolean, Object, Array],
      default: null,
    },
    /**
     * Check state is neither true nor false, used for check/uncheck all status for table headers
     */
    indeterminate: {
      type: Boolean,
      default: false,
    },
    /**
     * It is used to store the value of the checkbox so it can be saved in the model
     */
    value: {
      type: [String, Boolean, Object, Number],
      default: null,
    },
    /**
     * Style variation to give additional meaning.
     * `primary, secondary, tertiary`
     */
    variation: {
      type: String,
      default: 'primary',
      validator: (value) => {
        return value.match(/(primary|secondary|tertiary)/);
      },
    },
    /**
     * toggles checkbox's disabled state
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
    * Emitted when input value is changed
    */
    'update:checked',
  ],
  computed: {
    canCheck() {
      if (Array.isArray(this.checked)) {
        return this.checked.some((v) => isEqual(this.value, v));
      }
      return this.checked;
    },
    variationClass() {
      return this.disabled ? '' : this.variation;
    },
    labelClass() {
      return ['form-check-label', this.disabled && 'sb-disabled', this.indeterminate && 'indeterminate'].filter(Boolean);
    },
  },
  methods: {
    emitNewArray() {
      const arrCopy = this.checked.slice();
      if (arrCopy.some((v) => isEqual(this.value, v))) {
        arrCopy.splice(arrCopy.findIndex((v) => isEqual(this.value, v)), 1);
      } else {
        arrCopy.push(this.value);
      }
      this.$emit('update:checked', arrCopy);
    },

    inputChanged() {
      if (Array.isArray(this.checked)) this.emitNewArray();
      else if (this.$options.props.value) this.$emit('update:checked', this.value ?? this.checked);
      else this.$emit('update:checked', !this.checked);
    },
    inputChangedNext(value) {
      this.$emit('update:checked', value);
    },
  },
};

</script>
<style lang='scss' scoped>
@import '@src/components/Checkbox/style/sb-checkbox.scss';
</style>
